import { Group } from "components/Circle/ViewGroupImproved";
import { apiSlice } from "./apiSlice";

const CIRCLE_URL = "/circles";

// Public Circle Types
export type CircleRequestState =
  | "sending"
  | "idle"
  | "pending"
  | "sent"
  | "error";

export interface PublicCircle {
  id: string;
  title: string;
  description: string;
  monthlyContribution: number;
  currency: string;
  minMembers: number;
  maxMembers: number;
  nextPayout: string;
  createdAt: string;
  memberCount: number;
  memberImages: string[];
  status?: CircleRequestState;
  intervalUnit?: "day" | "week" | "month";
  intervalCount?: number;
}

export interface PublicCirclesResponse {
  circles: PublicCircle[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

// Sorting types for public circles
export type PublicCircleSortField = "monthlyContribution" | "maxMembers";
export type SortOrder = "asc" | "desc";

export interface GetPublicCirclesParams {
  limit?: number;
  page?: number;
  sortBy?: PublicCircleSortField;
  sortOrder?: SortOrder;
  search?: string;
}

export interface JoinRequestUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  displayImg: string | null;
}

export interface PendingJoinRequest {
  id: string;
  createdAt: string;
  User: JoinRequestUser;
}

export type JoinRequestDecision = "accepted" | "rejected";

// Public landing-page preview for a shareable invite link (/join/:token).
// Served without auth; carries no ids and only a masked invitee email.
export interface InvitePreview {
  circle: {
    title: string;
    contribution: number;
    currency: string;
    intervalUnit: "day" | "week" | "month";
    intervalCount: number;
    acceptedCount: number;
    maxMembers: number | null;
  };
  inviter: { name: string; displayImg: string | null } | null;
  invitee: { emailHint: string | null; hasAccount: boolean };
}

export const circleApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    group: builder.mutation({
      query: (data) => ({
        url: `${CIRCLE_URL}/create`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Circle", id: "LIST" }],
    }),
    groupInfo: builder.query<Group, { id: string }>({
      query: (id) => ({
        url: `${CIRCLE_URL}/${id}`,
        method: "GET",
      }),
      providesTags: (result) =>
        result ? [{ type: "Circle", id: result.id }] : [],
    }),
    getMembers: builder.query({
      query: (id) => ({
        url: `${CIRCLE_URL}/getMembers/${id}`,
        method: "GET",
      }),
      providesTags: (id) => [{ type: "CircleMembers", id: String(id) }],
    }),
    groupRequest: builder.mutation({
      query: ({ groupId, userId, decision }) => ({
        url: `${CIRCLE_URL}/decision`,
        method: "PATCH",
        body: {
          groupId,
          userId,
          decision,
        },
      }),
      invalidatesTags: ({ groupId }) => [{ type: "Circle", id: groupId }],
    }),
    activateGroup: builder.mutation({
      query: ({ groupId }) => ({
        url: `${CIRCLE_URL}/start`,
        method: "POST",
        body: {
          groupId,
        },
      }),
      invalidatesTags: ({ groupId }) => [{ type: "Circle", id: groupId }],
    }),
    getPayouts: builder.query({
      query: (id) => ({
        url: `${CIRCLE_URL}/payouts/${id}`,
        method: "GET",
      }),
    }),
    getMonthlyOwing: builder.query({
      query: (id) => ({
        url: `${CIRCLE_URL}/monthly/${id}`,
        method: "GET",
      }),
    }),
    getDisplayImages: builder.mutation({
      query: (id) => ({
        url: `${CIRCLE_URL}/images/${id}`,
        method: "GET",
      }),
    }),
    groupStart: builder.mutation({
      query: (groupId) => ({
        url: `${CIRCLE_URL}/start/${groupId}`,
        method: "GET",
      }),
    }),
    inviteToCircle: builder.mutation({
      query: ({ circleId, email }) => ({
        url: `${CIRCLE_URL}/invite`,
        method: "POST",
        body: { circleId, email },
      }),
      invalidatesTags: (_r, _e, { circleId }) => [
        { type: "CircleMembers", id: circleId },
        { type: "CircleMembers", id: `PENDING_${circleId}` },
      ],
    }),
    getInvitePreview: builder.query<InvitePreview, { token: string }>({
      query: ({ token }) => ({
        url: `${CIRCLE_URL}/join/${encodeURIComponent(token)}`,
        method: "GET",
      }),
    }),
    userSearch: builder.query<any, { email: string; circleId?: string }>({
      query: ({ email, circleId }) => {
        const params = new URLSearchParams({
          q: email,
        });
        if (circleId !== undefined) {
          params.append("circleId", circleId);
        }

        return {
          url: `${CIRCLE_URL}/search?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    deleteMember: builder.mutation({
      query: ({ groupId, userId }) => {
        const params = new URLSearchParams({
          groupId: String(groupId),
          userId: String(userId),
        });
        return {
          url: `${CIRCLE_URL}/deleteMember?${params.toString()}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ({ groupId }) => [
        { type: "CircleMembers", id: groupId },
      ],
    }),

    // Public Circles Endpoints
    getPublicCircles: builder.query<
      PublicCirclesResponse,
      GetPublicCirclesParams
    >({
      query: ({ limit = 20, page = 1, sortBy, sortOrder, search }) => {
        const params = new URLSearchParams();
        params.append("limit", String(limit));
        params.append("page", String(page));
        if (sortBy) {
          params.append("sortBy", sortBy);
        }
        if (sortOrder) {
          params.append("sortOrder", sortOrder);
        }
        if (search) {
          params.append("search", search);
        }
        return {
          url: `${CIRCLE_URL}/public?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "Circle", id: "PUBLIC_LIST" }],
    }),

    requestToJoin: builder.mutation<{ message: string }, { circleId: string }>({
      query: ({ circleId }) => ({
        url: `${CIRCLE_URL}/join-request`,
        method: "POST",
        body: { circleId },
      }),
      invalidatesTags: [{ type: "Circle", id: "PUBLIC_LIST" }],
    }),

    withdrawJoinRequest: builder.mutation<
      { message: string },
      { circleId: string }
    >({
      query: ({ circleId }) => ({
        url: `${CIRCLE_URL}/join-request`,
        method: "DELETE",
        body: { circleId },
      }),
      invalidatesTags: [{ type: "Circle", id: "PUBLIC_LIST" }],
    }),

    getPendingRequests: builder.query<
      PendingJoinRequest[],
      { groupId: string }
    >({
      query: ({ groupId }) => ({
        url: `${CIRCLE_URL}/pending-requests/${groupId}`,
        method: "GET",
      }),
      providesTags: (result, error, { groupId }) => [
        { type: "CircleMembers", id: `PENDING_${groupId}` },
      ],
    }),

    handleJoinRequest: builder.mutation<
      { message: string },
      { groupId: string; userId: string; decision: JoinRequestDecision }
    >({
      query: ({ groupId, userId, decision }) => ({
        url: `${CIRCLE_URL}/handle-request`,
        method: "PATCH",
        body: { groupId, userId, decision },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "CircleMembers", id: `PENDING_${groupId}` },
        { type: "Circle", id: groupId },
      ],
    }),
  }),
});

export const {
  useGroupStartMutation,
  useGetDisplayImagesMutation,
  useGetMonthlyOwingQuery,
  useGetPayoutsQuery,
  useGroupMutation,
  useGroupInfoQuery,
  useGroupRequestMutation,
  useGetMembersQuery,
  useActivateGroupMutation,
  useUserSearchQuery,
  useInviteToCircleMutation,
  useGetInvitePreviewQuery,
  useDeleteMemberMutation,
  // Public Circles
  useGetPublicCirclesQuery,
  useRequestToJoinMutation,
  useWithdrawJoinRequestMutation,
  useGetPendingRequestsQuery,
  useHandleJoinRequestMutation,
} = circleApiSlice;
